/**
 * Shared badges and category constants for the bank transactions panels.
 *
 * Both the "Documents" view and the "All Transactions" view render the same
 * category, direction, and counterparty tags — keeping them in one module
 * guarantees they look identical across the two panels.
 */

import { Badge } from "@/ui/primitives/badge";
import {
  TYPE_BADGE_COLOR,
  TYPE_LABELS,
  VOUCH_CONFIG,
  type VouchStatusKey,
} from "./badges.helpers";

export function TypeBadge({ type }: { type: string }) {
  return (
    <Badge variant="soft" size="sm" color={TYPE_BADGE_COLOR[type] ?? "gray"}>
      {TYPE_LABELS[type] ?? type}
    </Badge>
  );
}

export function DirectionBadge({ direction }: { direction: string }) {
  return (
    <Badge
      variant="soft"
      size="sm"
      color={direction === "credit" ? "green" : "red"}
    >
      {direction}
    </Badge>
  );
}

export function VouchBadge({ status }: { status: VouchStatusKey | undefined }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const cfg = VOUCH_CONFIG[status];
  return (
    <Badge variant="soft" size="sm" color={cfg.color} title={cfg.tooltip}>
      {status === "vouched" ? "✓ " : "⚠ "}
      {cfg.label}
    </Badge>
  );
}

export function CounterpartyBadge({
  name,
}: {
  name: string | undefined | null;
}) {
  if (!name || name === "-") {
    return <span className="text-xs text-muted-foreground">-</span>;
  }
  return (
    <Badge
      variant="outline"
      size="sm"
      className="max-w-[160px] truncate font-normal"
      title={name}
    >
      {name}
    </Badge>
  );
}
